import { type UserTrainingMedia, UserTrainingMediaActionType } from '@cobliteam/api-lib/src/__generated__/graphql';
import GraphQLTrainingVideoRequester from '@cobliteam/api-lib/src/dashboard/trainingVideos/GraphQLTrainingVideoRequester';
import type { TrainingVideo, TrainingVideosByCategory, UUID } from '@cobliteam/common-types';
import { type UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import upsertUserTrainingMedia from './upsertUserTrainingMedia';

export const QUERY_KEY = 'TRAINING_VIDEOS';

// Lazy instantiation to avoid circular dependency issues
const getTrainingVideoGraphQlRequester = () => new GraphQLTrainingVideoRequester();

const USER_TRAINING_MEDIA_KEY = [`${QUERY_KEY}_USER_TRAINING_MEDIA`];

const useTrainingVideo = () => {
  const queryClient = useQueryClient();

  const useTrainingVideos = ({ ...options }: Partial<UseQueryOptions<TrainingVideo[]>>) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    const queryKey = [QUERY_KEY];

    return useQuery<TrainingVideo[]>({
      ...options,
      queryKey,
      queryFn: () => getTrainingVideoGraphQlRequester().getTrainingVideos(),
    });
  };

  const useAllTrainingVideosByCategory = ({ ...options }: Partial<UseQueryOptions<TrainingVideosByCategory[]>>) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    const queryKey = [`${QUERY_KEY}_BY_CATEGORY`];

    return useQuery<TrainingVideosByCategory[]>({
      ...options,
      queryKey,
      queryFn: () => getTrainingVideoGraphQlRequester().getAllTrainingVideosByCategory(),
    });
  };

  const useUserTrainingMedia = ({ ...options }: Partial<UseQueryOptions<UserTrainingMedia[]>>) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    const queryKey = [`${QUERY_KEY}_USER_TRAINING_MEDIA`];

    return useQuery<UserTrainingMedia[]>({
      ...options,
      queryKey,
      queryFn: () => getTrainingVideoGraphQlRequester().getUserTrainingMedia(),
    });
  };

  const useStartUserTrainingMedia = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<UserTrainingMedia, unknown, UUID>({
      mutationFn: async (trainingMediaId: UUID) => await getTrainingVideoGraphQlRequester().updateUserTrainingMedia(trainingMediaId, UserTrainingMediaActionType.Start),
      onSuccess: (data: UserTrainingMedia) => {
        queryClient.setQueryData(USER_TRAINING_MEDIA_KEY, (old: UserTrainingMedia[] | undefined) => upsertUserTrainingMedia(old, data));
      },
    });
  };

  const useFinishUserTrainingMedia = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<UserTrainingMedia, unknown, UUID>({
      mutationFn: async (trainingMediaId: UUID) => await getTrainingVideoGraphQlRequester().updateUserTrainingMedia(trainingMediaId, UserTrainingMediaActionType.Finish),
      onSuccess: (data: UserTrainingMedia) => {
        queryClient.setQueryData(USER_TRAINING_MEDIA_KEY, (old: UserTrainingMedia[] | undefined) => upsertUserTrainingMedia(old, data));
      },
    });
  };

  return {
    useTrainingVideos,
    useAllTrainingVideosByCategory,
    useUserTrainingMedia,
    useStartUserTrainingMedia,
    useFinishUserTrainingMedia,
  };
};

export default useTrainingVideo;
